import {
  DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS,
  INSTAGRAM_GRAPH_DESCRIPTION,
  normalizePlatformKey,
} from "./socialNetworkCatalog"

/** Parent/aggregate networks — not one of the 5 dashboard channels. */
const EXCLUDED_PAID_SOCIAL_NETWORKS = new Set(["meta", "mhs"])

function paidRowSocialNetwork(row) {
  return String(row?.social_network ?? row?.socialNetwork ?? "").trim().toLowerCase()
}

function paidRowConcretePlatformKey(row) {
  const key = normalizePlatformKey(row?.platform ?? row?.platformKey ?? "")
  return key && isAllowedPaidPlatformKey(key) ? key : ""
}

/** Objective-level API rows are rolled up across Meta channels (no ad/campaign/platform). */
function isPaidObjectiveAggregateRow(row) {
  if (!String(row?.objective ?? "").trim()) return false
  const adId = row?.ad_id ?? row?.adId
  const campaignId = row?.campaign_id ?? row?.campaignId
  const hasAd = adId != null && String(adId).trim() !== ""
  const hasCampaign = campaignId != null && String(campaignId).trim() !== ""
  return !hasAd && !hasCampaign
}

/** Aggregate parent networks (meta, mhs) — exclude unless API provides a platform. */
export function isExcludedPaidSocialNetwork(row) {
  if (!EXCLUDED_PAID_SOCIAL_NETWORKS.has(paidRowSocialNetwork(row))) return false
  if (isPaidObjectiveAggregateRow(row)) return false
  const platform = normalizePlatformKey(row?.platform ?? row?.platformKey ?? "")
  return !platform
}

function isAllowedPaidPlatformKey(platformKey) {
  const key = String(platformKey ?? "").trim().toLowerCase()
  return DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS.includes(key)
}

function filterAllowedPaidRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => !isExcludedPaidSocialNetwork(row))
}

/** ISO date for GET …/clients/{id}/paid?start_date=&end_date= */
export function formatPaidPerformanceDate(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ""
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export const PAID_REPORT_LEVELS = ["ad", "campaign", "objective"]
export const PAID_REPORT_SORT_BY = [
  "post_engagement",
  "reach",
  "revenues",
  "purchases",
  "start_date",
  "end_date",
]
export const PAID_REPORT_PAGE_SIZE = 10
export const PAID_REPORT_ALL_DATA_PAGE_SIZE = 50

export function paidViewToLevel(view) {
  if (view === "campaigns" || view === "performance") return "campaign"
  if (view === "objectives") return "objective"
  return "ad"
}

/** Dashboard sort buttons → API sort_by. */
export function paidUiSortToApiSort(sortBy) {
  if (sortBy === "revenue") return "revenues"
  if (sortBy === "reach") return "reach"
  if (sortBy === "engagement" || sortBy === "post_engagement") return "post_engagement"
  if (sortBy === "purchases") return "purchases"
  if (sortBy === "spend") return "post_engagement"
  return "post_engagement"
}

export function buildClientPaidReportQuery({
  level = "ad",
  id = "all",
  startDate,
  endDate,
  limit = PAID_REPORT_PAGE_SIZE,
  sortBy,
  after,
} = {}) {
  const params = {
    level: PAID_REPORT_LEVELS.includes(level) ? level : "ad",
    id: id == null || id === "" ? "all" : String(id),
  }
  if (startDate) params.start_date = startDate
  if (endDate) params.end_date = endDate
  if (limit != null) params.limit = String(limit)
  if (sortBy && PAID_REPORT_SORT_BY.includes(sortBy)) {
    params.sort_by = sortBy
  }
  if (after) params.after = String(after)
  return new URLSearchParams(params).toString()
}

/** Turn `paging.next` into a GET path under /api/v1 (keeps after= and the rest of the query). */
export function paidReportRequestPath(nextOrPath) {
  if (!nextOrPath || typeof nextOrPath !== "string") return null
  const raw = nextOrPath.trim()
  if (!raw) return null
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const url = new URL(raw)
      const marker = "/api/v1/"
      const idx = url.pathname.indexOf(marker)
      const path = idx >= 0
        ? url.pathname.slice(idx + marker.length)
        : url.pathname.replace(/^\//, "")
      return `${path}${url.search}`
    } catch {
      return null
    }
  }
  return raw.replace(/^\/api\/v1\//, "").replace(/^\//, "")
}

export function buildPaidPerformanceFetchKey(
  clientId,
  startDate,
  endDate,
  { level = "ad", sortBy = "", id = "all" } = {},
) {
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}|${level}|${sortBy}|${id}`
}

function emptyPaidPaging() {
  return { after: null, next: null, hasMore: false, pageResults: 0 }
}

export function unwrapPaidReportPaging(data) {
  const paging = data?.paging
  if (!paging || typeof paging !== "object") return emptyPaidPaging()
  let after = paging.cursors?.after ?? null
  const next = paging.next ?? null
  if (!after && typeof next === "string" && next.includes("after=")) {
    try {
      const url = new URL(next, "https://local.invalid")
      after = url.searchParams.get("after")
    } catch {
      after = null
    }
  }
  return {
    after: after || null,
    next: next || null,
    hasMore: Boolean(next || after),
    pageResults: Number(paging.page_results) || 0,
  }
}

export function unwrapPaidPerformancePayload(data) {
  if (Array.isArray(data?.data)) {
    return { rows: data.data, paging: unwrapPaidReportPaging(data) }
  }
  if (Array.isArray(data)) {
    return { rows: data, paging: emptyPaidPaging() }
  }
  return { rows: [], paging: emptyPaidPaging() }
}

function parseMetricNewValue(results, metricName) {
  if (!metricName) return 0
  const entry = results?.[metricName]
  if (!entry || typeof entry !== "object") return 0
  const raw = entry.new_value ?? entry.newValue
  if (raw == null || raw === "") return 0
  const num = Number(raw)
  return Number.isFinite(num) ? num : 0
}

export function computePaidRoas(revenue, spend) {
  const rev = Number(revenue) || 0
  const sp = Number(spend) || 0
  if (sp <= 0) return 0
  return rev / sp
}

export function formatPaidObjectiveName(raw) {
  if (!raw) return "Unknown"
  const stripped = String(raw).replace(/^OUTCOME_/i, "").replace(/_/g, " ").trim()
  return stripped
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

/** Stable key for merging the same objective across ads/channels within a platform filter. */
export function normalizePaidObjectiveKey(raw) {
  if (!raw) return ""
  return String(raw).trim().toUpperCase().replace(/^OUTCOME_/i, "")
}

function paidMediaUrl(row) {
  const raw =
    row?.ad_media_link ??
    row?.adMediaLink ??
    row?.thumbnail_url ??
    row?.thumbnailUrl ??
    row?.image_url ??
    row?.imageUrl ??
    null
  if (typeof raw !== "string") return null
  const url = raw.trim()
  if (!url || url === "null" || url === "undefined") return null
  return url
}

function formatFromMediaUrl(url) {
  if (!url || typeof url !== "string") return null
  const path = url.split("?")[0].toLowerCase()
  const match = path.match(/\.([a-z0-9]+)$/)
  if (!match) return null
  const ext = match[1]
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "Image"
  if (["mp4", "mov", "webm", "m4v"].includes(ext)) return "Video"
  return ext.toUpperCase()
}

function paidAdFormat(row, mediaUrl) {
  const raw = row?.ad_format ?? row?.adFormat ?? row?.format ?? row?.creative_type ?? row?.creativeType
  if (raw && String(raw).trim()) {
    return String(raw).replace(/_/g, " ").trim()
  }
  return formatFromMediaUrl(mediaUrl) || "Image"
}

function parseMetricCostPerResult(results, metricName) {
  if (!metricName) return 0
  const entry = results?.[metricName]
  if (!entry || typeof entry !== "object") return 0
  const raw = entry.cost_per_result ?? entry.costPerResult
  if (raw == null || raw === "") return 0
  const num = Number(raw)
  return Number.isFinite(num) ? num : 0
}

function parsePostEngagementNewValue(results) {
  const raw = results?.post_engagement?.new_value
  if (raw == null || raw === "") return null
  const num = Number(raw)
  return Number.isFinite(num) ? num : null
}

function mapResultsToTableMetrics(results) {
  const purchases = parseMetricNewValue(results, "purchases")
  const impressions = parseMetricNewValue(results, "impressions")
  const reach = parseMetricNewValue(results, "reach")
  const linkClicks = parseMetricNewValue(results, "link_clicks")
  const newPostEngagement = parseMetricNewValue(results, "new_post_engagement")
  const engagementRate = parseMetricNewValue(results, "new_engagement_rate")

  return {
    purchases,
    impressions,
    reach,
    linkClicks,
    cpc: parseMetricCostPerResult(results, "link_clicks"),
    postEngagement: parsePostEngagementNewValue(results),
    cpe: parseMetricCostPerResult(results, "new_post_engagement"),
    engagementRate,
    newFollowers: parseMetricNewValue(results, "followers"),
    shares: parseMetricNewValue(results, "post_shares"),
    saves: parseMetricNewValue(results, "post_saves"),
    videoViews: parseMetricNewValue(results, "post_video_views"),
    pe: newPostEngagement,
  }
}

function paidRowPlatformKey(row) {
  const fromPlatform = paidRowConcretePlatformKey(row)
  if (fromPlatform) return fromPlatform
  const rawPlatform = normalizePlatformKey(row?.platform ?? row?.platformKey ?? "")
  if (rawPlatform) return rawPlatform
  if (isExcludedPaidSocialNetwork(row)) return ""
  const fromNetwork = normalizePlatformKey(row?.social_network ?? row?.socialNetwork ?? "")
  if (fromNetwork && isAllowedPaidPlatformKey(fromNetwork)) return fromNetwork
  return fromNetwork || ""
}

function paidCampaignGroupKey(campaignId, platformKey) {
  const id = String(campaignId ?? "").trim()
  if (!id) return ""
  const platform = String(platformKey ?? "").trim()
  return platform ? `${id}|${platform}` : id
}

function paidRowDuplicateRank(row) {
  const sn = String(row?.social_network ?? row?.socialNetwork ?? "").toLowerCase()
  if (sn === "facebook" || sn === INSTAGRAM_GRAPH_DESCRIPTION) return 0
  if (sn === "instagram") return 1
  if (sn === "meta") return 2
  return 3
}

function paidAdDedupeKey(row) {
  if (row?.fromApi && row?.id) return String(row.id)
  const adId = row?.ad_id ?? row?.adId
  if (adId == null || String(adId).trim() === "") return null
  const platform = paidRowPlatformKey(row)
  return platform ? `${adId}|${platform}` : String(adId)
}

function paidCampaignDedupeKey(row) {
  if (row?.fromApi && row?.id) return String(row.id)
  const campaignId = row?.campaign_id ?? row?.campaignId
  if (campaignId == null || String(campaignId).trim() === "") return null
  return paidCampaignGroupKey(campaignId, paidRowPlatformKey(row))
}

function dedupePaidRowsByKey(rows, getKey) {
  const list = Array.isArray(rows) ? rows : []
  const bestByKey = new Map()
  for (const row of list) {
    const key = getKey(row)
    if (!key) continue
    const existing = bestByKey.get(key)
    if (!existing || paidRowDuplicateRank(row) < paidRowDuplicateRank(existing)) {
      bestByKey.set(key, row)
    }
  }

  const seen = new Set()
  const out = []
  for (const row of list) {
    const key = getKey(row)
    if (!key) {
      out.push(row)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push(bestByKey.get(key) || row)
  }
  return out
}

export function uniqueMappedPaidAds(ads = []) {
  return dedupePaidRowsByKey(filterAllowedPaidRows(ads), (ad) => {
    if (ad?.adId != null && String(ad.adId).trim() !== "") return String(ad.adId)
    if (ad?.id != null && String(ad.id).trim() !== "") return String(ad.id)
    return null
  })
}

function mapPaidPerformanceEntityRow(row, { id, displayName, campaignName, platform }) {
  const spend = parseMetricNewValue(row?.results, "spend")
  const revenue = parseMetricNewValue(row?.results, "revenues")
  const apiRoas = parseMetricNewValue(row?.results, "return_on_ad_spend")
  const roas = apiRoas
  const objectiveKey = normalizePaidObjectiveKey(row?.objective ?? "")
  const platformKey = paidRowPlatformKey({
    ...row,
    platform: platform ?? row?.platform,
    platformKey: row?.platformKey,
  })
  const campaignId = row?.campaign_id ?? row?.campaignId ?? ""
  const resolvedCampaignName =
    campaignName ?? row?.campaign_name ?? row?.campaignName ?? ""
  const mediaUrl = paidMediaUrl(row)

  return {
    id,
    adId: String(row?.ad_id ?? row?.adId ?? ""),
    title: displayName || resolvedCampaignName || "—",
    adName: row?.ad_name ?? row?.adName ?? "",
    format: paidAdFormat(row, mediaUrl),
    platform: platform ?? row?.platform ?? "",
    platformKey,
    roas,
    revenue,
    spend,
    image: mediaUrl,
    campaignId: String(campaignId),
    campaignName: resolvedCampaignName || "—",
    objective: formatPaidObjectiveName(objectiveKey || row?.objective),
    objectiveId: objectiveKey,
    fromApi: true,
    ...mapResultsToTableMetrics(row?.results),
  }
}

export function mapPaidPerformanceAdRow(row) {
  const campaignId = row?.campaign_id ?? row?.campaignId ?? ""
  const campaignName = row?.campaign_name ?? row?.campaignName ?? ""
  const adName = row?.ad_name ?? row?.adName ?? ""
  const displayName = adName || campaignName || "—"
  const platformKey = paidRowPlatformKey(row)
  const adId = row?.ad_id ?? row?.adId ?? row?.entity_id ?? `${campaignId}-${adName}`
  const apiRowId = row?.id
  const socialNetwork = String(row?.social_network ?? row?.socialNetwork ?? "").trim()

  return mapPaidPerformanceEntityRow(row, {
    id:
      apiRowId != null && String(apiRowId).trim() !== ""
        ? String(apiRowId)
        : [adId, platformKey, socialNetwork].filter(Boolean).join("|") || String(adId),
    displayName,
    campaignName,
    platform: row?.platform || platformKey,
  })
}

export function mapPaidPerformanceCampaignRow(row, adsForPlatformHint = []) {
  const campaignId = row?.campaign_id ?? row?.campaignId ?? ""
  const campaignName = row?.campaign_name ?? row?.campaignName ?? "—"
  const inferredPlatform =
    row?.platform ||
    (Array.isArray(adsForPlatformHint)
      ? adsForPlatformHint.find(
          (ad) =>
            String(ad?.campaign_id ?? ad?.campaignId ?? "") === String(campaignId) &&
            paidRowPlatformKey(ad) === paidRowPlatformKey(row),
        )?.platform
      : null)
  const platformKey = paidRowPlatformKey({ ...row, platform: inferredPlatform || row?.platform })

  const apiRowId = row?.id
  return mapPaidPerformanceEntityRow(row, {
    id:
      apiRowId != null && String(apiRowId).trim() !== ""
        ? String(apiRowId)
        : paidCampaignGroupKey(campaignId, platformKey) || String(campaignId),
    displayName: campaignName,
    campaignName,
    platform: inferredPlatform || platformKey,
  })
}

export function mapPaidPerformanceAds(ads = [], options = {}) {
  const keepAll = Boolean(options.keepAllRows)
  const source = keepAll ? (Array.isArray(ads) ? ads : []) : filterAllowedPaidRows(ads)
  const rows = keepAll ? source : dedupePaidRowsByKey(source, paidAdDedupeKey)
  return rows
    .map((row) =>
      row?.fromApi && !keepAll ? row : mapPaidPerformanceAdRow(row),
    )
    .filter((row) => keepAll || isAllowedPaidPlatformKey(row?.platformKey))
}

export function mapPaidPerformanceCampaigns(campaigns = [], adsForPlatformHint = [], options = {}) {
  const keepAll = Boolean(options.keepAllRows)
  const source = keepAll
    ? (Array.isArray(campaigns) ? campaigns : [])
    : filterAllowedPaidRows(campaigns)
  const rows = keepAll ? source : dedupePaidRowsByKey(source, paidCampaignDedupeKey)
  return rows.map((row) =>
    row?.fromApi && !keepAll
      ? row
      : mapPaidPerformanceCampaignRow(row, adsForPlatformHint),
  )
}

export function mapPaidPerformanceObjectiveRow(row) {
  const objectiveKey = normalizePaidObjectiveKey(row?.objective ?? "")
  const platformKey = paidRowPlatformKey(row)
  return mapPaidPerformanceEntityRow(row, {
    id: objectiveKey || String(row?.id || row?.entity_id || "objective"),
    displayName: formatPaidObjectiveName(objectiveKey || row?.objective),
    campaignName: row?.campaign_name ?? row?.campaignName ?? "",
    platform: row?.platform || platformKey,
  })
}

export function mapPaidPerformanceObjectives(objectives = []) {
  const rows = filterAllowedPaidRows(objectives)
  return rows
    .map((row) => (row?.fromApi ? row : mapPaidPerformanceObjectiveRow(row)))
    .filter((row) => !row?.platformKey || isAllowedPaidPlatformKey(row.platformKey))
}

export function paidCampaignRowsToSummaries(campaigns = []) {
  return mapPaidPerformanceCampaigns(campaigns, [], { keepAllRows: true }).map((row) => ({
    id: row.id,
    campaignId: String(row.campaignId || ""),
    platformKey: row.platformKey || "",
    name: row.campaignName || row.title || "—",
    objective: row.objective,
    objectiveId: row.objectiveId,
    ads: [],
    adCount: 0,
    topAd: null,
    spend: row.spend,
    revenue: row.revenue,
    roas: row.roas,
  }))
}

export function paidObjectiveRowsToSummaries(objectives = []) {
  return mapPaidPerformanceObjectives(objectives).map((row) => ({
    id: row.objectiveId || row.id,
    name: row.objective || row.title || "—",
    campaignCount: 0,
    adCount: 0,
    topCampaign: null,
    spend: row.spend,
    revenue: row.revenue,
    roas: row.roas,
    platformKey: row.platformKey || "",
  }))
}

function sortPaidAdsForTop(ads) {
  return [...ads].sort((a, b) => {
    const roasCmp = (Number(b?.roas) || 0) - (Number(a?.roas) || 0)
    if (roasCmp !== 0) return roasCmp
    return (Number(b?.spend) || 0) - (Number(a?.spend) || 0)
  })
}

/**
 * Campaign cards: title from campaign_name, spend/revenue/ROAS from campaigns[].results.*.new_value
 * when present. Ads are used for unique ad count and the Top Ad footer.
 */
export function buildPaidCampaignSummaries(ads = [], { objectiveFilter = null, campaigns = [] } = {}) {
  const uniqueAds = uniqueMappedPaidAds(ads)
  const filtered = objectiveFilter
    ? uniqueAds.filter((ad) => ad.objective === objectiveFilter)
    : uniqueAds

  const campaignById = new Map()
  for (const row of mapPaidPerformanceCampaigns(campaigns, ads)) {
    const key = row.id || paidCampaignGroupKey(row.campaignId, row.platformKey)
    if (!key) continue
    campaignById.set(key, row)
  }

  const campaignMap = new Map()
  for (const ad of filtered) {
    const id = paidCampaignGroupKey(ad.campaignId, ad.platformKey)
    if (!id) continue
    if (!campaignMap.has(id)) {
      const overlay = campaignById.get(id)
      campaignMap.set(id, {
        id,
        campaignId: String(ad.campaignId || overlay?.campaignId || ""),
        platformKey: ad.platformKey || overlay?.platformKey || "",
        name: overlay?.campaignName || ad.campaignName || "—",
        objective: overlay?.objective || ad.objective,
        objectiveId: overlay?.objectiveId || ad.objectiveId,
        ads: [],
      })
    }
    campaignMap.get(id).ads.push(ad)
  }

  for (const [id, overlay] of campaignById) {
    if (campaignMap.has(id)) continue
    if (objectiveFilter && overlay.objective !== objectiveFilter) continue
    if ((Number(overlay.spend) || 0) <= 0 && (Number(overlay.revenue) || 0) <= 0) continue
    campaignMap.set(id, {
      id,
      campaignId: String(overlay.campaignId || ""),
      platformKey: overlay.platformKey || "",
      name: overlay.campaignName || "—",
      objective: overlay.objective,
      objectiveId: overlay.objectiveId,
      ads: [],
    })
  }

  return Array.from(campaignMap.values())
    .map((campaign) => {
      const overlay = campaignById.get(campaign.id)
      const sorted = sortPaidAdsForTop(campaign.ads)
      const spend = overlay
        ? overlay.spend
        : campaign.ads.reduce((sum, ad) => sum + (Number(ad.spend) || 0), 0)
      const revenue = overlay
        ? overlay.revenue
        : campaign.ads.reduce((sum, ad) => sum + (Number(ad.revenue) || 0), 0)
      const roas = overlay ? overlay.roas : 0

      return {
        ...campaign,
        adCount: campaign.ads.length,
        topAd: sorted[0] || null,
        spend,
        revenue,
        roas,
      }
    })
    .sort((a, b) => (Number(b.roas) || 0) - (Number(a.roas) || 0))
}

/**
 * Rows for Advertising Performance table: distinct objectives from API, then matching ads
 * (and campaign-only rows when no ad exists for that campaign).
 */
export function buildPaidAdvertisingPerformanceRows({
  ads = [],
  campaigns = [],
  objectives = [],
  platforms,
} = {}) {
  const rawAds = Array.isArray(ads) ? ads : []
  const rawCampaigns = Array.isArray(campaigns) ? campaigns : []

  const mappedAds = mapPaidPerformanceAds(rawAds)
  const platformAdRows = filterMappedAdsByPlatforms(mappedAds, platforms)

  const campaignKeysWithAds = new Set(
    platformAdRows
      .map((row) => paidCampaignGroupKey(row.campaignId, row.platformKey))
      .filter(Boolean),
  )

  const platformCampaignRows = filterRawCampaignsByPlatforms(rawCampaigns, rawAds, platforms)

  const campaignOnlyRows = platformCampaignRows
    .filter((row) => {
      const key = paidCampaignGroupKey(
        row?.campaign_id ?? row?.campaignId,
        paidRowPlatformKey(row),
      )
      return key && !campaignKeysWithAds.has(key)
    })
    .map((row) => mapPaidPerformanceCampaignRow(row, rawAds))

  const platformCampaignMapped = filterMappedAdsByPlatforms(campaignOnlyRows, platforms)

  return [...platformAdRows, ...platformCampaignMapped]
}

function filterMappedAdsByPlatforms(ads, platforms) {
  const showAll = platforms?.has("all") || !platforms || platforms.size === 0
  if (showAll) return ads
  return ads.filter((ad) => platforms.has(ad.platformKey))
}

function filterRawAdsByPlatform(ads, platforms) {
  const mapped = mapPaidPerformanceAds(ads)
  return filterMappedAdsByPlatforms(mapped, platforms)
}

function platformsShowAll(platforms) {
  return platforms?.has("all") || !platforms || platforms.size === 0
}

/** Campaign rows on the selected platform(s). Same campaign_id can appear on Facebook and Instagram. */
function filterRawCampaignsByPlatforms(campaignRows, _rawAds, platforms) {
  const rows = dedupePaidRowsByKey(
    filterAllowedPaidRows(campaignRows),
    paidCampaignDedupeKey,
  )
  if (platformsShowAll(platforms)) return rows
  return rows.filter((row) => platforms.has(paidRowPlatformKey(row)))
}

export function buildPaidObjectiveSummaries({ ads = [], campaigns = [], objectives = [], platforms } = {}) {
  const platformFilteredAds = filterRawAdsByPlatform(ads, platforms)
  const campaignRows = Array.isArray(campaigns) ? campaigns : []
  const platformCampaignRows = filterRawCampaignsByPlatforms(campaignRows, ads, platforms)

  const byObjective = new Map()

  for (const ad of platformFilteredAds) {
    const objectiveKey = ad.objectiveId
    if (!objectiveKey) continue
    let entry = byObjective.get(objectiveKey)
    if (!entry) {
      entry = {
        objectiveKey,
        spend: 0,
        revenue: 0,
        adCount: 0,
        campaignIds: new Set(),
      }
      byObjective.set(objectiveKey, entry)
    }
    entry.spend += ad.spend
    entry.revenue += ad.revenue
    entry.adCount += 1
    if (ad.campaignId) {
      entry.campaignIds.add(paidCampaignGroupKey(ad.campaignId, ad.platformKey))
    }
  }

  for (const row of platformCampaignRows) {
    const objectiveKey = normalizePaidObjectiveKey(row?.objective ?? "")
    if (!objectiveKey) continue
    const campaignKey = paidCampaignDedupeKey(row)
    if (!campaignKey) continue

    let entry = byObjective.get(objectiveKey)
    if (!entry) {
      const spend = parseMetricNewValue(row?.results, "spend")
      const revenue = parseMetricNewValue(row?.results, "revenues")
      if (spend <= 0 && revenue <= 0) continue
      entry = {
        objectiveKey,
        spend,
        revenue,
        adCount: 0,
        campaignIds: new Set(),
      }
      byObjective.set(objectiveKey, entry)
    } else if (!entry.campaignIds.has(campaignKey) && entry.adCount === 0) {
      entry.spend += parseMetricNewValue(row?.results, "spend")
      entry.revenue += parseMetricNewValue(row?.results, "revenues")
    }
    entry.campaignIds.add(campaignKey)
  }

  return [...byObjective.values()]
    .map((entry) => {
      const spend = entry.spend
      const revenue = entry.revenue
      return {
        id: entry.objectiveKey,
        name: formatPaidObjectiveName(entry.objectiveKey),
        campaignCount: entry.campaignIds.size,
        adCount: entry.adCount,
        spend,
        revenue,
        roas: computePaidRoas(revenue, spend),
      }
    })
    .filter((row) => row.adCount > 0 || row.campaignCount > 0)
}

/** Shared numeric sort for paid media widgets (ROAS, revenue, spend). */
export function sortPaidMediaByMetric(items, sortBy, sortDir) {
  return [...items].sort((a, b) => {
    const cmp = (Number(a[sortBy]) || 0) - (Number(b[sortBy]) || 0)
    return sortDir === "desc" ? -cmp : cmp
  })
}
